import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "../ui/textarea";
import { ControlCombobox } from "../ui/ControlCombobox";
import { MultiUserCombobox } from "../ui/MultiUserCombobox";
import { SingleUserCombobox } from "../ui/SingleUserCombobox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ScrollArea } from "@/components/ui/scroll-area";
import type { Audit } from "@/app/pages/auditcentre";
import { MCMMappingsCard, type MappingLink } from "@/components/mcm/MCMMappingsCard";
import { useTenancy } from "@/contexts/TenancyContext";
// DED-1887: Supporting documents for audits
import { AuditLinkedDocumentsSection } from "./AuditLinkedDocumentsSection";
type AuditEditDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  audit: Audit | null;
  onSave: (audit: Omit<Audit, 'id' | 'findings' | 'incidents'> & { controls?: string[] }) => void;
  isSubmitting?: boolean;
  availableOwners: string[];
};

export function AuditEditDialog({
  open,
  onOpenChange,
  audit,
  onSave,
  isSubmitting = false,
  availableOwners = []
}: AuditEditDialogProps) {
  const { activeTenancy } = useTenancy();
  const [auditData, setAuditData] = useState<Omit<Audit, 'id' | 'findings' | 'incidents'> & { controls?: string[]; linkedDocuments?: string[] }>({
    type: '',
    scope: '',
    owners: [],
    auditor: '',
    controls: [],
    linkedDocuments: [],
  });

  useEffect(() => {
    if (audit) {
      setAuditData({
        type: audit.type || '',
        scope: audit.scope || '',
        owners: Array.isArray(audit.owners) ? [...audit.owners] : [],
        auditor: audit.auditor || '',
        controls: audit.controls || [],
        linkedDocuments: audit.linkedDocuments || [],
      });
    }
  }, [audit]);

  const handleSave = (e: React.FormEvent) => {
    e.preventDefault();
    onSave(auditData);
  };

  const defaultEntityLink: MappingLink | undefined = audit && (audit as any).id ? {
    id: `internal:audit:${String((audit as any).id)}`,
    ref: String((audit as any).id),
    title: audit.scope || audit.type || 'Audit',
    domain: 'Audit',
  } : undefined;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className={`max-w-3xl! w-full max-h-[90vh] overflow-y-auto rounded-xl shadow-lg border border-white/10 p-0!`} aria-describedby={undefined}>
        <div className="flex flex-col gap-4 h-full bg-card">
          <div className="sticky top-0 z-20 py-6 border-b px-4 border-white/10 bg-gradient-to-b from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60">
            <DialogTitle className="text-foreground">Edit Audit</DialogTitle>
          </div>
          <div className="space-y-4 px-4 pb-4">
            <div>
              <label className="block text-sm font-medium mb-1">Audit Type</label>
              <Select value={auditData.type.toLowerCase()} onValueChange={(value) => setAuditData({ ...auditData, type: value })}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select Audit Type" />
                </SelectTrigger>
                <SelectContent className="w-full">
                  <SelectItem value="internal">Internal</SelectItem>
                  <SelectItem value="external">External</SelectItem>
                  <SelectItem value="compliance">Compliance</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              {/* DED-1141: Changed from 'Scope Statement' to 'Audit Scope' */}
              <label className="block text-sm font-medium mb-1">Audit Scope</label>
              <Textarea
                value={auditData.scope}
                onChange={(e) => setAuditData({ ...auditData, scope: e.target.value })}
                placeholder="Scope of the audit"
              />
            </div>
            <div className="grid grid-cols-1 items-center gap-4">
              <Accordion type="single" collapsible defaultValue="controls">
                <AccordionItem value="controls" className="border rounded-lg shadow-sm mb-2 bg-white dark:bg-background">
                  <AccordionTrigger className="px-4 py-3 text-base font-medium flex items-center gap-2 mb-4">
                    <span className="flex-1 text-left">Controls / Clauses in Scope</span>
                    <span className="ml-auto text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                      {Array.isArray(auditData.controls) ? `${auditData.controls.length} selected` : 'None selected'}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <ScrollArea className="max-h-[520px] overflow-auto">
                      <ControlCombobox
                        selected={auditData.controls || []}
                        setSelected={(value) => setAuditData({ ...auditData, controls: (value as Array<string | number>).map(String) })}
                        className="border border-gray-200 rounded-md focus:border-primary focus:ring-1 focus:ring-primary w-full"
                      />
                    </ScrollArea>
                  </AccordionContent>
                </AccordionItem>
                <MCMMappingsCard
                  tenancyId={activeTenancy?.id}
                  isReadOnly={false}
                  controlIds={(auditData.controls || []).map(String)}
                  defaultEntityLink={defaultEntityLink}
                  onEnsureControlLinked={(controlId) => setAuditData((prev) => ({
                    ...prev,
                    controls: Array.from(new Set([...(prev.controls || []), String(controlId)])),
                  }))}
                  collapsible
                />
              </Accordion>
              <div>
                <label className="block text-sm font-medium mb-1">Auditor</label>
                <Input
                  value={auditData.auditor}
                  onChange={(e) => setAuditData({ ...auditData, auditor: e.target.value })}
                  placeholder="Auditor name"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Owners</label>
                <MultiUserCombobox
                  selected={auditData.owners}
                  setSelected={(value) => setAuditData({ ...auditData, owners: value })}
                />
              </div>

              {/* DED-1887: Supporting Documents Section */}
              <div className="py-2">
                <AuditLinkedDocumentsSection
                  linkedDocuments={auditData.linkedDocuments || []}
                  onDocumentsChange={(docs) => setAuditData({ ...auditData, linkedDocuments: docs })}
                  inheritedControls={auditData.controls || []}
                  inheritedFrameworks={auditData.frameworks || []}
                  auditTitle={auditData.scope || auditData.type}
                  auditOwners={auditData.owners || []}
                />
              </div>
            </div>
          </div>
          <DialogFooter className="mt-4 p-4 sticky bottom-0 z-20 border-white/10 bg-gradient-to-t from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60 border-t flex! flex-row! justify-between! items-center! align-center! gap-2">
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={isSubmitting || !auditData.type || !auditData.scope || !auditData.auditor}
            >
              Save Changes
            </Button>
          </DialogFooter>

        </div>
      </DialogContent>
    </Dialog>
  );
}
